import React from 'react'
import classNames from 'classnames'
import { launchToast, ToastTypes } from '../components'
import { WemoloLoading, WemoloTypography } from '../components/Wemolo'
import { useSetDeviceForceUpdate, useUpdateDeviceConfigMutation } from './DeviceConfig/utils'
import { useGetDeviceData } from './StationView/utils/station-hooks'
import styles from './OverrideTraficLight.module.css'

const DEVICE_UUID = 'balena_4c82e007ffbf98baf878c534a1278ca6'
export const OverrideTraficLight = () => {
    const { data: deviceData, refetch } = useGetDeviceData(DEVICE_UUID)
    const config = deviceData?.device[0]?.config
    const config_target_timestamp = deviceData?.device[0]?.config_target_timestamp

    const { setDeviceForceUpdate, loadingForceUpdate } = useSetDeviceForceUpdate(() => {
        launchToast(ToastTypes.Success, 'Successfully force updated config!')
    })

    const { updateDeviceConfig, loading } = useUpdateDeviceConfigMutation()
    const busy = loading || loadingForceUpdate

    const handleLightClick = async (light: string) => {
        if (busy) return
        try {
            await updateDeviceConfig({
                device_uuid: DEVICE_UUID,
                diff_config: convertLightToConfig(light, config?.diff),
                timestamp: config_target_timestamp ?? ''
            })
            await refetch()
            await setDeviceForceUpdate({ device_uuid: DEVICE_UUID })
        } catch {
            // toasts surfaced by hooks
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const convertConfigToLight = (diffConfig: any) => {
        if (!diffConfig?.traffic_light_device?.overwrite_enable) {
            return 'disabled'
        }
        const overwrite_enable = diffConfig?.traffic_light_device.overwrite_enable
        if (overwrite_enable === true) {
            if (diffConfig?.traffic_light_device?.overwrite_value > 0) {
                return 'green'
            } else {
                return 'red'
            }
        }
        return 'disabled'
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const convertLightToConfig = (light: string, config: any) => {
        if (light === 'disabled') {
            return {
                ...config,
                traffic_light_device: {
                    ...config?.traffic_light_device,
                    overwrite_enable: false,
                    overwrite_value: 0
                }
            }
        }
        if (light === 'green') {
            return {
                ...config,
                traffic_light_device: {
                    ...config?.traffic_light_device,
                    overwrite_enable: true,
                    overwrite_value: 1
                }
            }
        }
        if (light === 'red') {
            return {
                ...config,
                traffic_light_device: {
                    ...config?.traffic_light_device,
                    overwrite_enable: true,
                    overwrite_value: 0
                }
            }
        }
        return {
            ...config,
            traffic_light_device: {
                ...config?.traffic_light_device,
                overwrite_enable: false,
                overwrite_value: 0
            }
        }
    }
    const activeLight = convertConfigToLight(config?.diff)

    return (
        <div className={styles.container}>
            {busy && (
                <div className={styles.loadingWrap}>
                    <WemoloLoading />
                </div>
            )}
            <WemoloTypography variant="headline-medium" tag="h1" className={styles.title}>
                Traffic Light Override for {DEVICE_UUID}
            </WemoloTypography>
            <div className={styles.grid}>
                <button
                    className={classNames(styles.button, styles.buttonRed, {
                        [styles.active]: activeLight === 'red'
                    })}
                    disabled={busy}
                    onClick={() => handleLightClick('red')}
                >
                    RED
                </button>

                <button
                    className={classNames(styles.button, styles.buttonGreen, {
                        [styles.active]: activeLight === 'green'
                    })}
                    disabled={busy}
                    onClick={() => handleLightClick('green')}
                >
                    GREEN
                </button>
                <button
                    className={classNames(styles.button, styles.buttonDisable, {
                        [styles.active]: activeLight === null
                    })}
                    disabled={busy}
                    onClick={() => handleLightClick('stopOverride')}
                >
                    Stop Override
                </button>
            </div>
            {activeLight && (
                <WemoloTypography
                    variant="body-small"
                    tag="div"
                    className={classNames(styles.statusBox, {
                        [styles.statusBoxRed]: activeLight === 'red',
                        [styles.statusBoxGreen]: activeLight === 'green'
                    })}
                >
                    Traffic light is currently set to: <strong>{activeLight.toUpperCase()}</strong>
                </WemoloTypography>
            )}
            {activeLight === null && (
                <WemoloTypography variant="body-small" tag="div" className={styles.statusBox}>
                    Traffic light override is stopped
                </WemoloTypography>
            )}
        </div>
    )
}
